import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import axios from 'axios'
import { isString } from 'lodash'
import { useFetch } from '#app'
import { type InferType, number, object, string } from 'yup'
import type { RaffleDataForAppointment } from '../types/raffle'
import { registerAuthInterceptor } from './authInterceptor'

import { registerErrorInterceptor } from './errorInterceptor'
import { makeUpdatePayload } from './updatePayload'
import type {
  AdditionalInfoObject,
  AlternativeContact,
  Appointment,
  AppointmentUpdateAction,
  AvailableTimeslot,
  OptIns,
  Timeslot
} from '~/terminierungsportal/types/appointmentTypes'
import { useAppStore } from '~/base/stores/app'
import { useAppointmentStore } from '~/terminierungsportal/stores/appointment'
import { useBaseUrl } from '#imports'
import type { FeedbackForm } from '~/base/types/forms/feedback-form'

export const technicianLocationResponseSchema = object({
  vin: string().required(),
  latitude: number().required(),
  longitude: number().required(),
  timestamp: string().required()
})

export type TechnicianLocationResponse = InferType<
  typeof technicianLocationResponseSchema
>

export type AuthInfo = {
  token: string
  postalCode: string
}

export interface AppointmentRequestBody {
  contactInfo?: {
    email?: string
    phone?: string
  }
  alternativeContact?: AlternativeContact
  additionalInfos?: AdditionalInfoObject
  preferredTimeslot?: Timeslot | null
  optIns?: OptIns
  action: string
}

interface RawTimeslot {
  from: string
  to: string
}

type RawAppointment = Omit<Appointment, 'timeslot' | 'preferredTimeslot'> & {
  timeslot: RawTimeslot
  preferredTimeslot: RawTimeslot | null
  prevTimeslot: RawTimeslot | null
}

export interface FeedbackFormForAppointment extends FeedbackForm {
  appointmentId: string
}

export class ApiClient {
  private authInfo: AuthInfo | null = null

  constructor(private readonly $axios: AxiosInstance) {
    this.authInfo = this.loadAuthInfo()

    registerAuthInterceptor(this.$axios, () => this.authInfo?.token ?? '')

    registerErrorInterceptor(this.$axios, () =>
      this.authenticate(this.authInfo?.postalCode ?? '')
    )
  }

  get appointmentId(): string {
    return useAppointmentStore().appointmentId
  }

  get storageKey(): string {
    return btoa(`apiClient.auth.${this.appointmentId}`)
  }

  isAuthenticated(): boolean {
    return this.authInfo !== null
  }

  async authenticate(postalCode: string): Promise<string> {
    try {
      this.clearAuthInfo()

      const { headers } = await this.$axios.post(this.path('authToken'), {
        postalCode
      })

      this.authInfo = {
        token: <string>headers['x-access-token'],
        postalCode
      }

      this.storeAuthInfo(this.authInfo)

      return this.authInfo.token
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('authenticate', error)
      }

      if (
        this.isAxiosError(error) &&
        !(error.response?.status === 404 || error.response?.status === 401)
      ) {
        useAppStore().requestFailed(error)
      }

      throw error
    }
  }

  async getAppointment(): Promise<Appointment | undefined> {
    try {
      return this.makeAppointmentResponse(
        await this.$axios.get(this.appointmentId)
      )
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('getAppointment', error)
        if (!this.is404(error)) {
          throw error
        }
      } else {
        console.error(error)
      }
      return undefined
    }
  }

  async getChildAppointment(): Promise<Appointment | undefined> {
    try {
      return this.makeAppointmentResponse(
        await this.$axios.get(this.path('childAppointment'))
      )
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('getAppointment', error)
        if (!this.is404(error)) {
          throw error
        }
      }
      return undefined
    }
  }

  async getTechnicianLocation(): Promise<TechnicianLocationResponse> {
    try {
      const { data } = await this.$axios.get(this.path('technician-location'))
      return data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('technicianLocation', error)
      }
      throw error
    }
  }

  async getTimeSlots(): Promise<AvailableTimeslot[]> {
    try {
      const {
        data: { timeslots }
      } = await this.$axios.get(this.path('timeslots'))
      // @todo validate and deserialize via typestack
      return timeslots.map(
        ({ from, till: to }: { from: unknown; till: unknown }) => ({ from, to })
      )
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('getTimeslots', error)
        if (!this.is404(error)) {
          throw error
        }
      }
      return []
    }
  }

  async getSmsText(smsType: string): Promise<string | undefined> {
    try {
      const {
        data: { message: smsText }
      } = await this.$axios.get(`${this.path('sms')}?smsType=${smsType}`)

      return smsText
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('getSmsText', error)
        if (!this.is404(error)) {
          throw error
        }
      }
      return undefined
    }
  }

  async getEmailText(emailType: string): Promise<string | undefined> {
    try {
      const {
        data: { message: emailText }
      } = await this.$axios.get(`${this.path('email')}?emailType=${emailType}`)

      return emailText
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('getEmailText', error)
        if (this.is404(error)) {
          return undefined
        }
      }
      throw error
    }
  }

  async putAppointmentFields(
    appointment: Partial<Appointment>
  ): Promise<Appointment> {
    try {
      const response = this.makeAppointmentResponse(
        await this.$axios.put(
          this.appointmentId,
          makeUpdatePayload(appointment)
        )
      )

      return response
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('putAppointmentFields', error)
      }
      throw error
    }
  }

  async putAppointmentStateUpdate(
    action: AppointmentUpdateAction
  ): Promise<Appointment> {
    try {
      return this.makeAppointmentResponse(
        await this.$axios.put(this.appointmentId, { action })
      )
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('putAppointmentStateUpdate', error)
      }
      throw error
    }
  }

  async publishAppointment(): Promise<void> {
    try {
      await this.$axios.post(this.path('publish'))
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('publishAppointment', error)
      }
      throw error
    }
  }

  async delegateAppointment(): Promise<Appointment> {
    try {
      return this.makeAppointmentResponse(
        await this.$axios.post(this.path('delegate'))
      )
    } catch (error) {
      if (axios.isAxiosError(error)) {
        this.logError('delegateAppointment', error)
      }
      throw error
    }
  }

  async fetchFeedbackId(appointmentId: string): Promise<string> {
    const { baseUrl } = useBaseUrl()
    const { data } = await useFetch<{ feedbackId: string }>(
      `${baseUrl}/terminierungsportal/feedback/feedbackIdForAppointment`,
      {
        method: 'GET',
        params: {
          appointmentId
        }
      }
    )
    return data.value?.feedbackId ?? ''
  }

  async submitFeedbackForm(
    feedback: FeedbackFormForAppointment
  ): Promise<string> {
    const { baseUrl } = useBaseUrl()
    const { data } = await useFetch<{ id: string }>(
      baseUrl + '/terminierungsportal/feedback/appointmentFeedback',
      {
        method: 'POST',
        body: feedback
      }
    )
    if (!data.value?.id) {
      console.warn('Missing id of submitted feedbackportal')
      return ''
    }

    return data.value.id
  }

  async submitRaffle(
    feedbackId: string,
    data: RaffleDataForAppointment
  ): Promise<void> {
    const { baseUrl } = useBaseUrl()
    await useFetch(
      `${baseUrl}/terminierungsportal/feedback/${feedbackId}/raffleForAppointment`,
      {
        method: 'POST',
        body: data
      }
    )
  }

  private loadAuthInfo(): AuthInfo | null {
    const storedAuthInfo = localStorage.getItem(this.storageKey)
    return isString(storedAuthInfo) ? JSON.parse(storedAuthInfo) || null : null
  }

  private clearAuthInfo(): void {
    localStorage.removeItem(this.storageKey)
  }

  private storeAuthInfo(authInfo: AuthInfo): void {
    localStorage.setItem(this.storageKey, JSON.stringify(authInfo))
  }

  private path(path: string): string {
    return `${this.appointmentId}/${path}`
  }

  private makeAppointmentResponse(response: AxiosResponse): Appointment {
    const appointment = response.data.appointment as RawAppointment
    const { timeslot, childShortUrl = '', ...props } = appointment

    const preferredTimeslot = this.parseTimeslot(appointment.preferredTimeslot)
    const prevTimeslot = this.parseTimeslot(appointment.prevTimeslot)

    return {
      ...props,
      childShortUrl,
      preferredTimeslot,
      prevTimeslot,
      timeslot: {
        from: new Date(timeslot.from),
        to: new Date(timeslot.to)
      }
    }
  }

  private parseTimeslot = (timeslot: RawTimeslot | null) => {
    let parsedTimeslot = null
    if (timeslot) {
      const { from, to } = timeslot
      parsedTimeslot = {
        from: new Date(from),
        to: new Date(to)
      }
    }
    return parsedTimeslot
  }

  private is404(error: AxiosError): boolean {
    return error.response?.status === 404
  }

  private logError(label: string, error: AxiosError): void {
    console.error(
      `[API][${label}] Received ${error.response?.status} for ${error.config.method} ${error.config.url}`
    )
  }

  private isAxiosError(error: AxiosError | any): error is AxiosError {
    return error && error.isAxiosError
  }
}
